import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VehicleData from "../../components/vehicleData";
import ClientData from "../../components/clientData";
import InputChecklist from "../../components/inputChecklist";
import DetailedChecklist from "../../components/detailedChecklist";
import Services from "../../components/services";
import OrderOfService from "../../components/orderOfService";

const STEPS = [
  { n: 1, label: "Cliente", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
  )},
  { n: 2, label: "Veículo", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-2a4 4 0 00-4-4h-3V5a1 1 0 00-1-1H3zm9 4h3l2 2v1h-5V8z" />
    </svg>
  )},
  { n: 3, label: "Checklist de Entrada", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
    </svg>
  )},
  { n: 4, label: "Checklist Obrigatório", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  )},
  { n: 5, label: "Orçamento", icon: (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
    </svg>
  )},
];

function CreateService() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [terminalMsg, setTerminalMsg] = useState("");
  const [searchParams] = useSearchParams();
  const s = searchParams.get("step");

  const roleMaxStep = useMemo(() => ({ RECEPCAO: 3, MECANICO: 4 }), []);
  const defaultMax = 6;
  const getMaxForRole = (r) => roleMaxStep[r] ?? defaultMax;

  const messagesByRole = {
    RECEPCAO: "Recepção efetuada. Aguardando checklist detalhado pela equipe de mecânicos.",
    MECANICO: "Checklist efetuado. Aguardando aprovação do chefe da oficina.",
  };

  const showTerminal = (r) => { const msg = messagesByRole[r]; if (msg) setTerminalMsg(msg); };

  const nextStep = (n) => {
    const max = getMaxForRole(role);
    if (n > max) { showTerminal(role); return; }
    setTerminalMsg(""); setStep(n);
  };

  useEffect(() => {
    const raw = localStorage.getItem("yc_usr_dt_ls");
    if (!raw) { window.location.href = "/login"; return; }
    try { setRole(JSON.parse(raw)?.role || ""); } catch { setRole(""); }
  }, []);

  useEffect(() => {
    const max = getMaxForRole(role);
    if (s) {
      const desired = parseInt(s, 10);
      if (Number.isFinite(desired)) {
        if (desired > max) showTerminal(role);
        else { setStep(desired); setTerminalMsg(""); }
      }
    }
  }, [s, role]);

  useEffect(() => {
    if (step > getMaxForRole(role)) showTerminal(role);
  }, [role, step]);

  const maxAllowed = getMaxForRole(role);

  return (
    <div className="flex flex-col self-stretch px-20 pt-8 pb-12 max-md:px-4">
      {/* Header */}
      <div className="flex items-center gap-3 py-4 px-6 bg-white rounded-lg shadow-sm">
        <Link to="/dashboard" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">Nova Ordem de Serviço</h1>
      </div>

      {/* Stepper */}
      {!terminalMsg && step < 7 && (
        <div className="mt-4 bg-white rounded-lg shadow-sm py-5 px-6">
          <div className="flex items-center justify-between max-md:hidden">
            {STEPS.map((s, idx) => {
              const isLocked = s.n > maxAllowed;
              const isActive = !isLocked && step === s.n;
              const isDone = !isLocked && step > s.n;
              const isLast = idx === STEPS.length - 1;

              return (
                <React.Fragment key={s.n}>
                  <div className="flex items-center gap-2.5">
                    {/* Circle */}
                    <div className={`flex items-center justify-center h-9 w-9 rounded-full text-sm font-semibold transition-all ${
                      isLocked ? "bg-gray-100 text-gray-300"
                      : isActive ? "bg-[#389E0D] text-white shadow-md shadow-green-200"
                      : isDone ? "bg-green-100 text-[#389E0D]"
                      : "bg-gray-100 text-gray-400"
                    }`}>
                      {isDone ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span>{s.n}</span>
                      )}
                    </div>
                    {/* Label */}
                    <span className={`text-sm font-medium whitespace-nowrap ${
                      isLocked ? "text-gray-300"
                      : isActive ? "text-gray-900"
                      : isDone ? "text-[#389E0D]"
                      : "text-gray-400"
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {/* Connector line */}
                  {!isLast && (
                    <div className={`flex-1 h-0.5 mx-3 rounded-full transition-all ${
                      isDone ? "bg-[#389E0D]"
                      : isLocked || s.n >= maxAllowed ? "bg-gray-200"
                      : "bg-gray-200"
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile stepper */}
          <div className="hidden max-md:flex items-center justify-between">
            <div className="flex items-center gap-2">
              {STEPS.map((s) => {
                const isLocked = s.n > maxAllowed;
                const isActive = !isLocked && step === s.n;
                const isDone = !isLocked && step > s.n;
                return (
                  <div key={s.n} className={`h-2 rounded-full transition-all ${
                    isLocked ? "w-2 bg-gray-200"
                    : isActive ? "w-8 bg-[#389E0D]"
                    : isDone ? "w-2 bg-[#389E0D]"
                    : "w-2 bg-gray-200"
                  }`} />
                );
              })}
            </div>
            <span className="text-sm font-medium text-gray-500">
              {step}/{Math.min(maxAllowed, STEPS.length)}
              <span className="ml-2 text-gray-900">{STEPS.find(s => s.n === step)?.label}</span>
            </span>
          </div>
        </div>
      )}

      {/* Terminal message */}
      {terminalMsg ? (
        <div className="flex flex-col items-center justify-center mt-6 bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#389E0D]" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Etapa concluída!</h2>
          <p className="text-gray-500 max-w-md">{terminalMsg}</p>
          <Link
            to="/dashboard"
            className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#389E0D] text-white font-medium hover:bg-green-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            Voltar ao dashboard
          </Link>
        </div>
      ) : (
        <div className="mt-4">
          {step === 1 ? <ClientData nextStep={nextStep} />
          : step === 2 ? <VehicleData nextStep={nextStep} />
          : step === 3 ? <InputChecklist nextStep={nextStep} />
          : step === 4 ? <DetailedChecklist nextStep={nextStep} />
          : step === 5 ? <Services nextStep={nextStep} />
          : step === 6 ? <OrderOfService nextStep={nextStep} />
          : null}
        </div>
      )}
    </div>
  );
}

export default CreateService;
